/**
 * Значки счетов.
 *
 * Счёт выбирают в спешке: в форме операции список открыт долю секунды, и
 * «Основная карта» рядом с «Основной счёт» читается дольше, чем рисуется
 * рисунок. Поэтому у счёта есть значок — иконка Font Awesome, тот же набор,
 * что во всём приложении и в макетах. Здесь хранится только имя иконки:
 * рисует её приложение.
 *
 * Своего значка у счёта может и не быть — тогда берётся значок вида. Это не
 * заглушка «нет иконки», а осмысленное умолчание: карта похожа на карту,
 * наличные на купюры, и человеку не нужно ничего выбирать, чтобы список стал
 * читаемым.
 *
 * До Font Awesome значками были эмодзи. Сохранённые раньше читаются через
 * `LEGACY_ACCOUNT_ICON`: выбор человека не теряется, а показывается новой
 * иконкой того же смысла.
 */
import type { AccountKind } from './types.js';

/** Умолчание по виду счёта. */
export const KIND_ICON: Record<AccountKind, string> = {
  debit: 'credit-card',
  cash: 'money-bill',
  savings: 'piggy-bank',
  credit_card: 'credit-card-front',
  deposit: 'vault',
  brokerage: 'chart-line',
  custodial: 'child',
};

/**
 * Из чего человек выбирает.
 *
 * Список закрыт: то, чем в быту называют деньги и места, где они лежат. Кроме
 * них в выборе значка есть логотипы банков и весь набор категорий — но
 * первыми идут эти: счёт ищут среди значков денег, а не среди продуктов.
 */
export const ACCOUNT_ICONS: readonly string[] = [
  'credit-card', 'money-bill', 'piggy-bank', 'building-columns', 'credit-card-front', 'vault',
  'chart-line', 'child', 'wallet', 'sack-dollar', 'coins', 'house', 'car', 'plane', 'gift', 'star',
  'money-bills', 'ruble-sign', 'dollar-sign', 'euro-sign', 'hand-holding-dollar', 'money-check-dollar',
  'landmark', 'umbrella', 'shield-heart', 'briefcase', 'graduation-cap', 'suitcase-rolling', 'mobile',
  'cart-shopping', 'heart', 'gem', 'baby-carriage', 'bitcoin-sign',
];

/** Эмодзи прежних версий → иконка того же смысла. */
export const LEGACY_ACCOUNT_ICON: Record<string, string> = {
  '💳': 'credit-card', '💵': 'money-bill', '🐷': 'piggy-bank', '🏦': 'building-columns',
  '🔻': 'credit-card-front', '🔒': 'vault', '📈': 'chart-line', '🧒': 'child', '👛': 'wallet',
  '💰': 'sack-dollar', '🪙': 'coins', '🏠': 'house', '🚗': 'car', '✈️': 'plane', '✈': 'plane',
  '🎁': 'gift', '⭐': 'star',
};

/** Значок счёта: свой либо по виду. Старый эмодзи переводится в иконку. */
export const accountIcon = (
  account: { kind: AccountKind; icon?: string },
): string => {
  const own = account.icon?.trim();
  if (own) return LEGACY_ACCOUNT_ICON[own] ?? own;
  return KIND_ICON[account.kind] ?? 'credit-card';
};
